import { useContext, useState, type Dispatch, type SetStateAction } from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
  Dropdown,
  Field,
  Option,
  Spinner,
  makeStyles,
  tokens,
} from '@fluentui/react-components';
import { BoxMultipleCheckmarkRegular, Dismiss24Regular } from '@fluentui/react-icons';

import { ToastContext } from '@shared/contexts';

import type { TSubmissionPackageMockConfig } from '../types';

type TProps = {
  config?: TSubmissionPackageMockConfig;
  open: boolean;
  setOpen: Dispatch<SetStateAction<boolean>>;
};

const GENERATE_PACKAGE_DURATION_MS = 3000;

const NOTIFIED_BODIES = [
  'TÜV NORD CERT GmbH',
  'TÜV SÜD Product Service GmbH',
  'DEKRA Certification GmbH',
  'TÜV Rheinland LGA Products GmbH',
  'DQS Medizinprodukte GmbH',
  'DNV MEDCERT GmbH',
  'mdc medical device certification GmbH',
  'SLG Prüf- und Zertifizierungs GmbH',
  'Berlin Cert GmbH',
  'SZUTEST Konformitätsbewertungsstelle GmbH',
  'QMD Services GmbH',
];

const useStyles = makeStyles({
  dialogSurface: {
    maxWidth: '420px',
  },
  dialogContent: {
    display: 'grid',
    gap: tokens.spacingVerticalM,
  },
  field: {
    width: '320px',
    maxWidth: '100%',
  },
  titleRow: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
  },
  titleIcon: {
    fontSize: '20px',
    lineHeight: 1,
  },
});

const SubmissionPackageDialog = ({ config, open, setOpen }: TProps) => {
  const classes = useStyles();
  const [isGenerating, setIsGenerating] = useState(false);
  const [notifiedBody, setNotifiedBody] = useState(config?.notifiedBody ?? '');
  const { show } = useContext(ToastContext);

  if (!config) return null;

  const notifiedBodies = Array.from(new Set([config.notifiedBody, ...NOTIFIED_BODIES]));

  const handleOpenChange = (nextOpen: boolean) => {
    if (!nextOpen && isGenerating) return;
    setOpen(nextOpen);
  };

  const handleGenerate = async () => {
    if (notifiedBody !== config.notifiedBody) {
      show('warning', 'This notified body is not configured yet.');
      return;
    }
    setIsGenerating(true);
    await new Promise((resolve) => window.setTimeout(resolve, GENERATE_PACKAGE_DURATION_MS));
    window.location.assign(config.targetUrl);
  };

  return (
    <Dialog open={open} onOpenChange={(_, data) => handleOpenChange(data.open)}>
      <DialogSurface className={classes.dialogSurface}>
        <DialogBody>
          <DialogTitle
            action={
              <Button
                appearance="subtle"
                aria-label="close"
                icon={<Dismiss24Regular />}
                disabled={isGenerating}
                onClick={() => handleOpenChange(false)}
              />
            }
          >
            <div className={classes.titleRow}>
              <BoxMultipleCheckmarkRegular className={classes.titleIcon} />
              <span>Generate submission package</span>
            </div>
          </DialogTitle>
          <DialogContent className={classes.dialogContent}>
            <Field label="Jurisdiction" className={classes.field}>
              <Dropdown value={config.jurisdiction} selectedOptions={[config.jurisdiction]} disabled={isGenerating}>
                <Option value={config.jurisdiction}>{config.jurisdiction}</Option>
              </Dropdown>
            </Field>
            <Field label="Notified body" className={classes.field}>
              <Dropdown
                value={notifiedBody}
                selectedOptions={[notifiedBody]}
                disabled={isGenerating}
                onOptionSelect={(_, data) => setNotifiedBody(data.optionValue ?? notifiedBody)}
              >
                {notifiedBodies.map((name) => (
                  <Option key={name} value={name} text={name}>
                    {name}
                  </Option>
                ))}
              </Dropdown>
            </Field>
          </DialogContent>
          <DialogActions>
            <Button
              appearance="primary"
              icon={isGenerating ? <Spinner size="tiny" /> : <BoxMultipleCheckmarkRegular />}
              disabled={isGenerating}
              onClick={handleGenerate}
            >
              Generate
            </Button>
            <Button appearance="secondary" disabled={isGenerating} onClick={() => handleOpenChange(false)}>
              Cancel
            </Button>
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
};

export default SubmissionPackageDialog;
